// Story 11.4 — Mapper puro de WS envelopes a `LogEntry`. Port simplificado
// del CLI event-formatter (Story 6.3) — ahora consumido también por el
// LogPanel del editor web.

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogEntry {
  /** id único — `${timestamp}::${seq}` para que React key sea estable. */
  id: string;
  level: LogLevel;
  /** ISO 8601 desde el evento (o now() si falta). */
  timestamp: string;
  /** Mensaje plano para mostrar en la fila. */
  message: string;
  /** Tipo del evento original (`flow:step-executed`, etc.). */
  eventType: string;
  /** Bot al que pertenece. */
  botId?: string;
  /** Contexto estructurado (flowId, stepId, userId, etc.). */
  context: Record<string, unknown>;
  /** True si se generó del lado cliente (acciones como Deploy). */
  fromClient?: boolean;
}

export interface WsEnvelopeShape {
  type?: string;
  event?: string;
  botId?: string | number;
  timestamp?: string;
  payload?: unknown;
}

const SUBSCRIBE_ACK_EVENTS = new Set([
  'subscribe-ack',
  'unsubscribe-ack',
  'connect-ack',
  'pong',
  'system:hello',
]);

// P-432 (review 11.4): el contador `seq` era global del módulo y compartido
// entre `envelopeToLog` y `clientLog`; en HMR/multi-LogPanel/test-isolation
// causaba colisiones de id. Agregamos un nonce de 4 chars per-id para
// garantizar unicidad inclusive si dos ids comparten timestamp+seq.
let seq = 0;
function nextId(timestamp: string): string {
  seq = (seq + 1) % Number.MAX_SAFE_INTEGER;
  const nonce = Math.random().toString(36).slice(2, 6);
  return `${timestamp}::${seq}::${nonce}`;
}

function toRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return value as Record<string, unknown>;
}

function asString(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

function severityFor(eventType: string, payload: Record<string, unknown>): LogLevel {
  if (eventType === 'flow:error') return 'error';
  // Story 20-2 — un envío que se reintenta (límite de Meta, red) es warn.
  if (eventType === 'message:failed') return payload.transient === true ? 'warn' : 'error';
  if (eventType === 'bot:stopped') {
    return payload.reason === 'error' ? 'error' : 'info';
  }
  if (eventType === 'queue:dead') return 'error';
  if (eventType === 'flow:step-executed') return 'debug';
  if (eventType === 'session:created' || eventType === 'session:expired') return 'debug';
  if (eventType === 'queue:retry' || eventType === 'queue:failed') return 'warn';
  if (eventType.startsWith('emergency:')) return 'warn';
  return 'info';
}

function summarizeMessage(eventType: string, payload: Record<string, unknown>): string {
  switch (eventType) {
    case 'message:received': {
      const from = asString(payload.from) ?? '?';
      const type = asString(payload.messageType) ?? asString(payload.type) ?? 'message';
      return `← [${from}] ${type}`;
    }
    case 'message:sent': {
      const to = asString(payload.to) ?? '?';
      const type = asString(payload.messageType) ?? asString(payload.type) ?? 'message';
      return `→ [${to}] ${type}`;
    }
    case 'message:failed': {
      const to = asString(payload.to) ?? '?';
      const msg = asString(payload.message) ?? 'no se entregó';
      return `✗ [${to}] ${msg}`;
    }
    case 'flow:step-executed': {
      const flowId = asString(payload.flowId) ?? '?';
      const stepId = asString(payload.stepId) ?? '?';
      const stepIndex = payload.stepIndex;
      return `flow:${flowId} → step:${stepId}${typeof stepIndex === 'number' ? ` (#${stepIndex})` : ''}`;
    }
    case 'flow:completed': {
      const flowId = asString(payload.flowId) ?? '?';
      const steps = payload.stepsExecuted;
      return `flow:${flowId} completado${typeof steps === 'number' ? ` (${steps} steps)` : ''}`;
    }
    case 'flow:error': {
      const flowId = asString(payload.flowId) ?? '?';
      const err = toRecord(payload.error);
      const msg = asString(err.message) ?? asString(payload.errorMessage) ?? 'error';
      return `flow:${flowId} ✗ ${msg}`;
    }
    case 'bot:started':
      return 'bot iniciado';
    case 'bot:stopped': {
      const reason = asString(payload.reason);
      return reason ? `bot detenido (${reason})` : 'bot detenido';
    }
    case 'session:created': {
      const phone = asString(payload.userPhone);
      return phone ? `sesión creada con ${phone}` : 'sesión creada';
    }
    case 'session:expired':
      return 'sesión expirada';
    case 'queue:retry': {
      const attempt = payload.attempt;
      return `queue retry${typeof attempt === 'number' ? ` #${attempt}` : ''}`;
    }
    case 'queue:dead':
      return 'queue → dead-letter';
    default:
      return eventType;
  }
}

/**
 * Convierte un envelope de WS a `LogEntry`. Retorna `null` para eventos
 * no-loggeables (acks de suscripción).
 */
export function envelopeToLog(envelope: WsEnvelopeShape): LogEntry | null {
  const eventType = envelope.event ?? envelope.type ?? '';
  if (!eventType) return null;
  if (SUBSCRIBE_ACK_EVENTS.has(eventType)) return null;
  const timestamp = envelope.timestamp ?? new Date().toISOString();
  const payload = toRecord(envelope.payload);
  const level = severityFor(eventType, payload);
  const message = summarizeMessage(eventType, payload);
  const botId = envelope.botId != null ? String(envelope.botId) : undefined;
  return {
    id: nextId(timestamp),
    level,
    timestamp,
    message,
    eventType,
    botId,
    context: payload,
  };
}

/**
 * Crea una entry "client-driven" (Deploy/Rollback feedback) con timestamp
 * actual y level configurable. La marca `fromClient` para que la UI
 * pueda diferenciar visualmente.
 */
export function clientLog(
  level: LogLevel,
  message: string,
  context: Record<string, unknown> = {},
): LogEntry {
  const timestamp = new Date().toISOString();
  return {
    id: nextId(timestamp),
    level,
    timestamp,
    message,
    eventType: 'client',
    context,
    fromClient: true,
  };
}

/** Formatea HH:MM:SS.mmm desde un ISO. */
export function shortTime(iso: string): string {
  if (iso) {
    const m = iso.match(/T(\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?)/);
    if (m?.[1]) return m[1];
  }
  return new Date().toISOString().slice(11, 23);
}
